import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getMyRole } from "@/lib/dr-sara.functions";
import { listMyTraumaCourses } from "@/lib/trauma/attempts.functions";
import { TRAUMA_UI, TRAUMA_PLAYER_UI } from "@/lib/trauma/ui-copy";
import { getTraumaAnalytics } from "@/lib/trauma/admin.functions";
import { useState } from "react";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

export const Route = createFileRoute("/_authenticated/admin/trauma")({
  beforeLoad: async ({ context }) => {
    const role = await context.queryClient.ensureQueryData(
      queryOptions({ queryKey: ["me", "role"], queryFn: () => getMyRole() }),
    );
    if (!role.isAdmin) throw redirect({ to: "/admin" });
  },
  head: () => ({ meta: [{ title: TRAUMA_UI.adminTitle }] }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: TraumaAdmin,
});

function TraumaAdmin() {
  const { data } = useQuery({ queryKey: ["trauma", "my-courses"], queryFn: () => listMyTraumaCourses() });
  return (
    <div className="mx-auto max-w-6xl p-5">
      <h1 className="font-display text-2xl font-extrabold">{TRAUMA_UI.adminTitle}</h1>
      <p className="mt-3 rounded-2xl bg-amber-100 px-4 py-3 text-sm font-bold text-amber-900">{TRAUMA_UI.adminHiddenNote}</p>
      <Link to="/courses" className="mt-6 inline-flex rounded-full bg-primary px-5 py-2 font-bold text-primary-foreground">
        {TRAUMA_UI.adminOpenCatalog}
      </Link>
      <ol className="mt-6 grid gap-2">
        {(data ?? []).map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-3 rounded-ds-lg bg-card p-3 shadow-soft">
            <span className="grid size-8 place-items-center rounded-full bg-surface-green text-sm font-extrabold">{c.order}</span>
            <span className="flex-1 font-bold">{c.title}</span>
            <Link
              to="/courses/trauma/$id"
              params={{ id: c.id }}
              className="text-sm font-bold underline-offset-4 hover:underline"
            >
              {TRAUMA_PLAYER_UI.openCoursePage}
            </Link>
            <Link
              to="/courses/trauma/$id/play"
              params={{ id: c.id }}
              className="rounded-full bg-surface-orange px-4 py-2 text-sm font-bold"
            >
              {TRAUMA_PLAYER_UI.openPlayer}
            </Link>
          </li>
        ))}
      </ol>
      <TraumaAnalyticsPanel />
    </div>
  );
}

type Range = "7d" | "28d" | "all";
const RANGES: { key: Range; label: string }[] = [
  { key: "7d", label: "آخر 7 أيام" },
  { key: "28d", label: "آخر 28 يومًا" },
  { key: "all", label: "كل الوقت" },
];

function TraumaAnalyticsPanel() {
  const [range, setRange] = useState<Range>("28d");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "trauma", "analytics", range],
    queryFn: () => getTraumaAnalytics({ data: { range } }),
  });
  const t = data?.totals;
  const stats: [string, number | string][] = t
    ? [
        ["مشتريات فردية", t.purchases],
        ["مشتريات الباقة", t.bundlePurchases],
        ["بدأن", t.starts],
        ["أمهات بدأن", t.startedUsers],
        ["أكملن", t.completions],
        ["نسبة الإكمال", `${t.completionRate}%`],
        ["نقرات الدورة التالية", t.nextClicks],
        ["نقرات الحجز", t.bookClicks],
        ["نقرات المشاركة", t.shareClicks],
        ["حجوزات خلال 14 يومًا بعد النتيجة", t.bookingsAfterResult],
      ]
    : [];
  return (
    <section className="mt-10" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-extrabold">تحليلات الدورات</h2>
        <div role="group" aria-label="الفترة" className="flex gap-1 rounded-full bg-card p-1 shadow-soft">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              aria-pressed={range === r.key}
              className={`rounded-full px-4 py-1.5 text-sm font-bold ${range === r.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {isError ? (
        <p className="mt-4 rounded-2xl bg-surface-orange px-4 py-3 text-sm font-bold">تعذّر تحميل الأرقام، حاولي مرة ثانية.</p>
      ) : isLoading || !t ? (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-ds-lg bg-muted" />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {stats.map(([label, v]) => (
              <div key={label} className="rounded-ds-lg bg-card p-4 shadow-soft">
                <p className="text-xs font-bold text-muted-foreground">{label}</p>
                <p className="mt-1 font-display text-2xl font-extrabold tabular-nums">{v}</p>
              </div>
            ))}
            <a href="/admin/pending-purchases" className="rounded-ds-lg bg-surface-orange p-4 shadow-soft hover:shadow-lift">
              <p className="text-xs font-bold">إيصالات بانتظار المراجعة</p>
              <p className="mt-1 font-display text-2xl font-extrabold tabular-nums">{t.receiptsWaiting}</p>
            </a>
          </div>
          <div className="mt-6 overflow-x-auto rounded-ds-lg bg-card shadow-soft">
            <table className="w-full min-w-[960px] text-sm tabular-nums">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-right">
                  {["الدورة", "مشتريات", "بدأن", "أمهات", "أكملن", "الإكمال", "آمن / انتباه / ترميم", "المحاور الأبرز", "التالية", "الحجز", "المشاركة"].map((h) => (
                    <th key={h} className="px-3 py-3 font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.courses.map((c) => (
                  <tr key={c.id} className="border-t border-border/60 align-top">
                    <td className="px-3 py-3 font-bold">{c.order}. {c.title}</td>
                    <td className="px-3 py-3">{c.purchases}</td>
                    <td className="px-3 py-3">{c.starts}</td>
                    <td className="px-3 py-3">{c.startedUsers}</td>
                    <td className="px-3 py-3">{c.completions}</td>
                    <td className="px-3 py-3">{c.completionRate}%</td>
                    <td className="px-3 py-3">{c.levels.safe} / {c.levels.attention} / {c.levels.repair}</td>
                    <td className="px-3 py-3">
                      {c.highlightAxes.length ? (
                        <ul className="grid gap-0.5">
                          {c.highlightAxes.slice(0, 3).map((a) => (
                            <li key={a.n}>{a.n}. {a.label} ({a.count})</li>
                          ))}
                        </ul>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-3">{c.nextClicks}</td>
                    <td className="px-3 py-3">{c.bookClicks}</td>
                    <td className="px-3 py-3">{c.shareClicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">الأرقام من قاعدة البيانات، لا من زيارات الصفحات.</p>
        </>
      )}
    </section>
  );
}
